(() => {
  'use strict';

  const SELECTORS = {
    gallery: '[data-reiando-product-gallery]',
    slide: '[data-reiando-gallery-slide]'
  };

  const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
  const instances = new WeakMap();

  class ReiandoProductGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.slides = Array.from(gallery.querySelectorAll(SELECTORS.slide));
      this.mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.currentIndex = 0;
      this.targetIndex = null;
      this.wrapper = null;
      this.prevButton = null;
      this.nextButton = null;
      this.dotsContainer = null;
      this.dots = [];
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.programmaticTimer = null;
      this.isMounted = false;

      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);

      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.addListener(this.handleMediaChange);
      }

      window.addEventListener('resize', this.handleResize, { passive: true });
      this.refreshMode();
    }

    refreshMode() {
      if (this.mobileQuery.matches) {
        this.mount();
      } else {
        this.unmount();
      }
    }

    mount() {
      if (this.isMounted || this.slides.length <= 1) return;

      this.wrapper = document.createElement('div');
      this.wrapper.className = 'reiando-product__gallery-shell';
      this.wrapper.setAttribute('data-reiando-gallery-shell', '');
      this.wrapper.setAttribute('role', 'region');
      this.wrapper.setAttribute('aria-label', '商品画像ギャラリー');

      this.gallery.parentNode.insertBefore(this.wrapper, this.gallery);
      this.wrapper.appendChild(this.gallery);

      this.prevButton = this.createArrowButton('prev');
      this.nextButton = this.createArrowButton('next');
      this.dotsContainer = this.createDots();

      this.wrapper.appendChild(this.prevButton);
      this.wrapper.appendChild(this.nextButton);
      this.wrapper.appendChild(this.dotsContainer);

      this.prevButton.addEventListener('click', this.handlePrevious);
      this.nextButton.addEventListener('click', this.handleNext);
      this.gallery.addEventListener('scroll', this.handleScroll, { passive: true });

      this.isMounted = true;
      this.currentIndex = this.getClosestIndex();
      this.updateControls();

      window.requestAnimationFrame(() => {
        this.scrollToIndex(this.currentIndex, 'auto');
      });
    }

    unmount() {
      if (!this.isMounted || !this.wrapper) return;

      this.prevButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.gallery.removeEventListener('scroll', this.handleScroll);

      this.cancelScheduledWork();
      this.gallery.scrollLeft = 0;

      this.wrapper.parentNode.insertBefore(this.gallery, this.wrapper);
      this.wrapper.remove();

      this.wrapper = null;
      this.prevButton = null;
      this.nextButton = null;
      this.dotsContainer = null;
      this.dots = [];
      this.currentIndex = 0;
      this.targetIndex = null;
      this.isMounted = false;
    }

    destroy() {
      this.unmount();
      window.removeEventListener('resize', this.handleResize);

      if (typeof this.mobileQuery.removeEventListener === 'function') {
        this.mobileQuery.removeEventListener('change', this.handleMediaChange);
      } else {
        this.mobileQuery.removeListener(this.handleMediaChange);
      }
    }

    createArrowButton(direction) {
      const isPrevious = direction === 'prev';
      const button = document.createElement('button');

      button.type = 'button';
      button.className = `reiando-product__gallery-arrow reiando-product__gallery-arrow--${direction}`;
      button.setAttribute(
        'aria-label',
        isPrevious ? '前の商品画像を表示' : '次の商品画像を表示'
      );
      button.innerHTML = isPrevious
        ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 4 7 12l8 8" /></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 4 8 8-8 8" /></svg>';

      return button;
    }

    createDots() {
      const container = document.createElement('div');
      container.className = 'reiando-product__gallery-dots';
      container.setAttribute('role', 'group');
      container.setAttribute('aria-label', '商品画像の選択');

      this.dots = this.slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'reiando-product__gallery-dot';
        dot.setAttribute('aria-label', `${index + 1}枚目の商品画像を表示`);
        dot.addEventListener('click', () => this.goTo(index));
        container.appendChild(dot);
        return dot;
      });

      return container;
    }

    handlePrevious() {
      this.goTo(this.currentIndex - 1);
    }

    handleNext() {
      this.goTo(this.currentIndex + 1);
    }

    handleScroll() {
      if (!this.isMounted || this.scrollFrame !== null) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        const closestIndex = this.getClosestIndex();

        if (this.targetIndex !== null) {
          const targetSlide = this.slides[this.targetIndex];
          const targetDistance = targetSlide
            ? Math.abs(targetSlide.offsetLeft - this.gallery.scrollLeft)
            : Number.POSITIVE_INFINITY;

          if (closestIndex !== this.targetIndex || targetDistance > 2) return;

          this.clearProgrammaticState();
        }

        if (closestIndex === this.currentIndex) return;

        this.currentIndex = closestIndex;
        this.updateControls();
      });
    }

    handleResize() {
      if (!this.isMounted || this.resizeFrame !== null) return;

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.scrollToIndex(this.currentIndex, 'auto');
        this.updateControls();
      });
    }

    handleMediaChange() {
      this.refreshMode();
    }

    goTo(index) {
      if (!this.isMounted) return;

      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      if (nextIndex === this.currentIndex && this.targetIndex === null) return;

      this.currentIndex = nextIndex;
      this.targetIndex = nextIndex;
      this.updateControls();
      this.scrollToIndex(nextIndex, this.getScrollBehavior());

      window.clearTimeout(this.programmaticTimer);
      this.programmaticTimer = window.setTimeout(() => {
        this.clearProgrammaticState();
        this.currentIndex = this.getClosestIndex();
        this.updateControls();
      }, 700);
    }

    scrollToIndex(index, behavior) {
      const slide = this.slides[index];
      if (!slide) return;

      this.gallery.scrollTo({
        left: slide.offsetLeft,
        behavior
      });
    }

    getClosestIndex() {
      const galleryLeft = this.gallery.scrollLeft;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - galleryLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    updateControls() {
      const isFirst = this.currentIndex === 0;
      const isLast = this.currentIndex === this.slides.length - 1;

      if (this.prevButton) {
        this.prevButton.hidden = isFirst;
        this.prevButton.disabled = isFirst;
      }

      if (this.nextButton) {
        this.nextButton.hidden = isLast;
        this.nextButton.disabled = isLast;
      }

      this.dots.forEach((dot, index) => {
        const isActive = index === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    clearProgrammaticState() {
      this.targetIndex = null;
      window.clearTimeout(this.programmaticTimer);
      this.programmaticTimer = null;
    }

    cancelScheduledWork() {
      if (this.scrollFrame !== null) {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      if (this.resizeFrame !== null) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }

      this.clearProgrammaticState();
    }

    getScrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }
  }

  function initializeGalleries(root = document) {
    const galleries = [];

    if (root instanceof Element && root.matches(SELECTORS.gallery)) {
      galleries.push(root);
    }

    galleries.push(...root.querySelectorAll(SELECTORS.gallery));

    galleries.forEach((gallery) => {
      if (instances.has(gallery)) return;
      instances.set(gallery, new ReiandoProductGallery(gallery));
    });
  }

  function destroyGalleries(root) {
    const galleries = [];

    if (root instanceof Element && root.matches(SELECTORS.gallery)) {
      galleries.push(root);
    }

    galleries.push(...root.querySelectorAll(SELECTORS.gallery));

    galleries.forEach((gallery) => {
      const instance = instances.get(gallery);
      if (!instance) return;
      instance.destroy();
      instances.delete(gallery);
    });
  }

  function initialize() {
    initializeGalleries(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeGalleries(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyGalleries(event.target);
  });
})();
